<template>
  <div>
    <PrivateChannel />
  </div>
</template>

<script>
/**
 * Exchange WebSocket (Pusher/Soketi). On close code 1006 (abnormal closure),
 * the connection was cut without a close frame — usually Nginx/proxy timeout.
 * Fix: set proxy_read_timeout and proxy_send_timeout to 86400s for socket subdomain.
 * See docs/nginx-socket-websocket.conf
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { pinia } from '@/pinia';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import PrivateChannel from '@/Store/Channels/Private/PrivateChannel.vue';

window.Pusher = Pusher;

const BACKOFF = {
  INITIAL_MS: 2000,
  MAX_MS: 60000,
  MULTIPLIER: 1.8,
  MIN_STABLE_MS: 10000,
};
const VISIBILITY_DEBOUNCE_MS = 500;
const PUSHER_ACTIVITY_TIMEOUT_MS = 120000;
const PUSHER_PONG_TIMEOUT_MS = 30000;

export default {
  name: 'Socket',
  components: { PrivateChannel },

  setup() {
    const socketStore = useSocketStore(pinia);
    const userStore = useUserStore(pinia);
    const socket = computed(() => socketStore.getSocket);
    const user = computed(() => userStore.getUser);

    const echo = ref(null);
    const isConnecting = ref(false);
    const reconnectTimer = ref(null);
    const visibilityTimer = ref(null);
    const backoffMs = ref(BACKOFF.INITIAL_MS);
    const lastConnectedAt = ref(0);

    const conn = () => echo.value?.connector?.pusher?.connection;
    const state = () => conn()?.state ?? '';

    const setSocketId = () => {
      try {
        const id = echo.value?.socketId?.() ?? null;
        socketStore.setSocket({ socket: id });
      } catch {
        socketStore.setSocket({ socket: null });
      }
    };

    const clearTimers = () => {
      if (reconnectTimer.value != null) {
        clearTimeout(reconnectTimer.value);
        reconnectTimer.value = null;
      }
      if (visibilityTimer.value != null) {
        clearTimeout(visibilityTimer.value);
        visibilityTimer.value = null;
      }
    };

    const resetBackoff = () => {
      backoffMs.value = BACKOFF.INITIAL_MS;
    };

    const nextBackoff = () => {
      const next = Math.round(backoffMs.value * BACKOFF.MULTIPLIER);
      backoffMs.value = Math.min(next, BACKOFF.MAX_MS);
      return backoffMs.value;
    };

    const createEcho = () => {
      if (window.Echo && window.Echo !== echo.value) {
        try {
          window.Echo.disconnect?.();
        } catch (_) {}
      }

      return new Echo({
        broadcaster: 'pusher',
        authEndpoint: '/api/broadcasting/auth',
        key: import.meta.env.VITE_PUSHER_APP_KEY,
        wsHost: import.meta.env.VITE_PUSHER_HOST,
        wsPort: Number(import.meta.env.VITE_PUSHER_PORT) || 443,
        wssPort: Number(import.meta.env.VITE_PUSHER_PORT) || 443,
        forceTLS: (import.meta.env.VITE_PUSHER_SCHEME || 'https') === 'https',
        encrypted: true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER || 'mt1',
        activityTimeout: PUSHER_ACTIVITY_TIMEOUT_MS,
        pongTimeout: PUSHER_PONG_TIMEOUT_MS,
      });
    };

    const bind = (connection, handlers) => {
      if (!connection) return;
      connection.bind('connected', handlers.connected);
      connection.bind('disconnected', handlers.disconnected);
      connection.bind('error', handlers.error);
      connection.bind('state_change', handlers.stateChange);
    };

    const unbind = (connection, handlers) => {
      if (!connection) return;
      try {
        connection.unbind('connected', handlers.connected);
        connection.unbind('disconnected', handlers.disconnected);
        connection.unbind('error', handlers.error);
        connection.unbind('state_change', handlers.stateChange);
      } catch (_) {}
    };

    const scheduleReconnect = () => {
      clearTimers();
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;

      let delay = backoffMs.value;
      const elapsed = lastConnectedAt.value > 0 ? Date.now() - lastConnectedAt.value : Infinity;
      if (elapsed < BACKOFF.MIN_STABLE_MS) {
        delay = Math.max(delay, BACKOFF.MIN_STABLE_MS - elapsed);
      }
      nextBackoff();

      reconnectTimer.value = setTimeout(() => {
        reconnectTimer.value = null;
        const s = state();
        if (s === 'connected' || s === 'connecting') return;
        connect(false);
      }, delay);
    };

    const handlers = {
      connected: () => {
        isConnecting.value = false;
        lastConnectedAt.value = Date.now();
        clearTimers();
        resetBackoff();
        setSocketId();
      },
      disconnected: () => {
        isConnecting.value = false;
        socketStore.setSocket({ socket: null });
        scheduleReconnect();
      },
      error: () => {
        isConnecting.value = false;
        scheduleReconnect();
      },
      stateChange: ({ current } = {}) => {
        if (current === 'unavailable' || current === 'failed') {
          isConnecting.value = false;
          socketStore.setSocket({ socket: null });
          scheduleReconnect();
        }
      },
    };

    const destroy = () => {
      clearTimers();
      isConnecting.value = false;
      const connection = conn();
      unbind(connection, handlers);
      const doDisconnect = () => {
        const activeEcho = echo.value || window.Echo;
        try {
          activeEcho?.disconnect?.();
        } catch (_) {}
        echo.value = null;
        window.Echo = null;
        socketStore.setSocket({ socket: null });
      };
      if (connection && state() === 'connecting') {
        const once = () => {
          try {
            connection.unbind('state_change', onStateChange);
            connection.unbind('connected', once);
          } catch (_) {}
          doDisconnect();
        };
        const onStateChange = ({ current } = {}) => {
          if (current === 'connected' || current === 'unavailable' || current === 'failed') once();
        };
        connection.bind('state_change', onStateChange);
        connection.bind('connected', once);
      } else {
        doDisconnect();
      }
    };

    const connect = (forceNew = false) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        scheduleReconnect();
        return;
      }
      if (isConnecting.value) return;

      if (forceNew || !echo.value) {
        destroy();
        echo.value = createEcho();
        window.Echo = echo.value;
        bind(conn(), handlers);
        resetBackoff();
      }

      const s = state();
      if (s === 'connected') {
        setSocketId();
        return;
      }
      if (s === 'connecting') return;

      clearTimers();
      isConnecting.value = true;
      try {
        echo.value?.connector?.pusher?.connect?.();
      } catch {
        isConnecting.value = false;
        destroy();
        echo.value = createEcho();
        window.Echo = echo.value;
        bind(conn(), handlers);
        try {
          isConnecting.value = true;
          echo.value?.connector?.pusher?.connect?.();
        } catch {
          isConnecting.value = false;
          scheduleReconnect();
        }
      }
    };

    const onOnline = () => connect(false);
    const onOffline = () => {
      socketStore.setSocket({ socket: null });
      clearTimers();
      isConnecting.value = false;
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (visibilityTimer.value) clearTimeout(visibilityTimer.value);
      visibilityTimer.value = setTimeout(() => {
        visibilityTimer.value = null;
        if (state() === 'connected' || state() === 'connecting') return;
        connect(false);
      }, VISIBILITY_DEBOUNCE_MS);
    };

    onMounted(() => {
      // Avoid forced reconnect churn on mount; reuse active connection when possible.
      connect(false);
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      document.addEventListener('visibilitychange', onVisibility);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibility);
      destroy();
    });

    watch(
      () => user.value?.id,
      // Private channel lifecycle already reacts to auth state;
      // do not force-create a new transport connection every time user id changes.
      () => connect(false)
    );

    return { socket, user };
  },
};
</script>
